import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from "react-i18next";

import { Modal } from '@lockerpm/design';

import {
  PlusOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import BoxData from "./components/access-key/BoxData";
import FormData from "./components/access-key/FormData";
import Review from "./components/access-key/Review";
import DeleteModal from "./components/access-key/Delete";
import SetupKeyModal from "./components/access-key/SetupKeyModal";

import global from "../../../config/global";
import accessKeyServices from "../../../services/access-key";
import { getRouterByLocation } from '../../../utils/common';

const ProjectAccessKey = () => {
  const { t } = useTranslation()
  const { PageHeader } = commonComponents;
  const { NoData } = itemsComponents;

  const currentPage = getRouterByLocation(window.location);
  const projectId = currentPage?.params?.project_id;

  const isReadOnly = useSelector((state) => state.project.isReadOnly)
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const createAccessKey = useSelector((state) => state.onboarding.createAccessKey)

  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [setupVisible, setSetupVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [response, setResponse] = useState(null);
  const [total, setTotal] = useState(0);
  const [params, setParams] = useState({ page: 1, size: 100 });
  const [data, setData] = useState([]);


  useEffect(() => {
    if (selectedProject && !selectedProject?.access_key_onboarding && selectedProject?.role !== global.constants.PROJECT_MEMBER_ROLE.READ_ONLY && data.length === 1 && !reviewVisible) {
      setSetupVisible(true)
    } else {
      setSetupVisible(false)
    }
  }, [selectedProject?.access_key_onboarding, data, reviewVisible])

  useEffect(() => {
    if (createAccessKey) {
      handleOpenForm()
    }
  }, [createAccessKey])

  useEffect(() => {
    fetchData()
  }, [projectId, JSON.stringify(params)])

  const isCreate = useMemo(() => {
    return data?.filter((d) => d.activated).length < 2
  }, [data])

  const fetchData = () => {
    setLoading(true)
    accessKeyServices.list(projectId, params).then((response) => {
      setData(response.results);
      setTotal(response.count)
      setLoading(false)
    }).catch(() => {
      setData([])
      setTotal(0)
      setLoading(false)
    })
  }

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const handleOpenReview = (response = null) => {
    setResponse(response);
    setReviewVisible(true)
  }

  const handleActivate = (item = null) => {
    Modal.confirm({
      title: t('common.confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('access_key.activate_question', { active: item?.activated ? t('button.deactivate') : t('button.activate')}),
      okText: item?.activated ? t('button.deactivate') : t('button.activate'),
      cancelText: t('button.cancel'),
      okButtonProps: {
        danger: true
      },
      onOk: () => {
        accessKeyServices.activate(projectId, item?.id, {
          activated: !item?.activated,
        }).then(() => {
          global.pushSuccess(t(`notification.success.access_key.${item?.activated ? 'deactivated' : 'activated'}`));
          fetchData();
        }).catch((error) => {
          global.pushError(error);
        })
      }
    })
  }

  const handleRegenerate = (item = null) => {
    Modal.confirm({
      title: t('common.confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('access_key.regenerate_question'),
      okText: t('button.regenerate'),
      cancelText: t('button.cancel'),
      okButtonProps: {
        danger: true
      },
      onOk: async () => {
        const accessKey = await global.jsCore.cryptoService.makeAccessKey(projectId)
        accessKeyServices.rotate(projectId, item?.id, {
          key: accessKey[0].encryptedString
        }).then(() => {
          global.pushSuccess(t(`notification.success.access_key.regenerated`));
          handleOpenReview({
            id: item?.id,
            access_key: accessKey[1].keyB64
          })
        }).catch((error) => {
          global.pushError(error);
        })
      }
    })
  }

  const handleDelete = (item = null) => {
    setSelectedItem(item);
    setDeleteVisible(true)
  }

  return (
    <div className="access-key layout-content">
      <PageHeader
        title={t('access_key.title')}
        subtitle={<Trans
          i18nKey={'access_key.description'}
          components={{ b: <b className="font-semibold"/> }}
        />}
        docLabel={t('access_key.set_up_and_use')}
        docOnboarding={t('onboarding.set_up_and_use_access_keys')}
        docClick={() => setSetupVisible(true)}
        actions={[
          {
            key: 'add',
            label: t('button.new_access_key'),
            type: 'primary',
            hide: isReadOnly,
            disabled: !isCreate,
            icon: <PlusOutlined />,
            click: () => handleOpenForm()
          }
        ]}
      />
      {
        total == 0 && <NoData
          loading={loading}
          className={'mt-6'}
        />
      }
      {
        total > 0 && <>
          <BoxData
            className="mt-6"
            loading={loading}
            data={data}
            params={params}
            isCreate={isCreate}
            onReload={fetchData}
            onUpdate={handleOpenForm}
            onActive={handleActivate}
            onRegenerate={handleRegenerate}
            onDelete={handleDelete}
          />
        </>
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        onClose={() => setFormVisible(false)}
        onReload={fetchData}
        onReview={handleOpenReview}
      />
      <Review
        visible={reviewVisible}
        response={response}
        onClose={() => setReviewVisible(false)}
      />
      <DeleteModal
        currentPage={currentPage}
        visible={deleteVisible}
        item={selectedItem}
        onClose={() => setDeleteVisible(false)}
        onReload={fetchData}
      />
      <SetupKeyModal
        visible={setupVisible}
        onClose={() => setSetupVisible(false)}
      />
    </div>
  );
}

export default ProjectAccessKey;